import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  LogOut,
  Search,
  MoreVertical,
  Mail,
  MessageSquare,
  FileUp,
  FileSignature,
  Download,
  Eye,
  ChevronDown,
  TrendingUp,
  Wallet,
  Clock,
  CheckCircle2,
  Loader2,
  Plus,
  RefreshCw,
  BarChart3,
  ArrowUpRight,
  Send,
  Inbox,
  History,
  Bell,
  BellOff,
  Circle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  new: 'bg-blue-100 text-blue-700 border-blue-200',
  contacted: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  qualified: 'bg-purple-100 text-purple-700 border-purple-200',
  docs_requested: 'bg-orange-100 text-orange-700 border-orange-200',
  docs_received: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  under_review: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  offer_sent: 'bg-teal-100 text-teal-700 border-teal-200',
  declined: 'bg-red-100 text-red-700 border-red-200',
  funded: 'bg-green-100 text-green-700 border-green-200'
};

const statusLabels = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  docs_requested: 'Docs Requested',
  docs_received: 'Docs Received',
  under_review: 'Under Review',
  offer_sent: 'Offer Sent',
  approved: 'Approved',
  declined: 'Declined',
  funded: 'Funded'
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Dashboard Overview Component with Analytics
function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingPreviews, setSendingPreviews] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/analytics`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSendPreviews = async () => {
    setSendingPreviews(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/send-template-previews`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Template previews sent!');
    } catch (error) {
      toast.error('Failed to send template previews');
    } finally {
      setSendingPreviews(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  const FUNNEL_COLORS = ['#1d4ed8', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'];
  const PIE_COLORS = ['#1d4ed8', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#818cf8', '#a78bfa'];

  const funnelData = analytics?.funnel ? [
    { name: 'Applied', value: analytics.funnel.total },
    { name: 'Contacted', value: analytics.funnel.contacted },
    { name: 'Documents', value: analytics.funnel.documents },
    { name: 'Offer', value: analytics.funnel.offers },
    { name: 'Approved', value: analytics.funnel.approved },
    { name: 'Funded', value: analytics.funnel.funded }
  ] : [];

  const statCards = [
    { label: 'Total Leads', value: stats?.total_leads || 0, icon: Users, bg: 'bg-blue-100', fg: 'text-blue-700' },
    { label: 'New This Week', value: stats?.recent_leads || 0, icon: ArrowUpRight, bg: 'bg-green-100', fg: 'text-green-700' },
    { label: 'Pipeline Value', value: formatCurrency(stats?.total_value || 0), icon: Wallet, bg: 'bg-purple-100', fg: 'text-purple-700' },
    { label: 'Avg. Loan', value: formatCurrency(analytics?.avg_loan_amount || 0), icon: TrendingUp, bg: 'bg-amber-100', fg: 'text-amber-700' },
    { label: 'Agreements Signed', value: `${analytics?.agreements?.signed || 0}/${analytics?.agreements?.total || 0}`, icon: FileSignature, bg: 'bg-indigo-100', fg: 'text-indigo-700' },
    { label: 'Funded', value: stats?.funded_leads || 0, icon: CheckCircle2, bg: 'bg-emerald-100', fg: 'text-emerald-700' }
  ];

  return (
    <div data-testid="dashboard-overview">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Dashboard</h1>
          <p className="text-slate-500 text-sm">Business performance overview</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/dashboard/leads">
            <Button className="bg-blue-700 hover:bg-blue-800" data-testid="view-leads-btn">
              <Users className="w-4 h-4 mr-2" /> All Leads
            </Button>
          </Link>
          <Link to="/dashboard/agreements">
            <Button variant="outline" data-testid="view-agreements-btn">
              <FileSignature className="w-4 h-4 mr-2" /> Agreements
            </Button>
          </Link>
          <Button 
            variant="outline" 
            onClick={handleSendPreviews} 
            disabled={sendingPreviews}
            data-testid="send-previews-btn"
          >
            {sendingPreviews ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            Email Previews
          </Button>
          <Button variant="outline" size="icon" onClick={fetchAll} data-testid="refresh-stats-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {statCards.map((stat, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`stat-card-${i}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">{stat.label}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1 font-['Outfit']">{stat.value}</p>
              </div>
              <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center`}>
                <stat.icon className={`w-5 h-5 ${stat.fg}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Monthly Leads Chart */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-700" /> Monthly Applications
          </h3>
          {analytics?.monthly?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={analytics.monthly}>
                <defs>
                  <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1d4ed8" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#1d4ed8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ borderRadius: 8, borderColor: '#e2e8f0' }} />
                <Area type="monotone" dataKey="leads" stroke="#1d4ed8" fillOpacity={1} fill="url(#colorLeads)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-400 text-center py-12">No data yet</p>
          )}
        </div>

        {/* Conversion Funnel */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-700" /> Conversion Funnel
          </h3>
          {funnelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ borderRadius: 8, borderColor: '#e2e8f0' }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {funnelData.map((_, i) => (
                    <Cell key={i} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-400 text-center py-12">No data yet</p>
          )}
        </div>
      </div>

      {/* Bottom Row: Business Types + Loan Types */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {analytics?.business_types?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">By Business Type</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={analytics.business_types} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={70} label={({type, count}) => `${type} (${count})`}>
                  {analytics.business_types.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
        {analytics?.loan_types?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">By Loan Type</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={analytics.loan_types} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={70} label={({type, count}) => `${type} (${count})`}>
                  {analytics.loan_types.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[(i + 2) % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

// Leads Management Component
function LeadsManagement() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', lead: null });
  const [actionMessage, setActionMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, [statusFilter, searchTerm]);

  const fetchLeads = async () => {
    try {
      let url = `${API_URL}/api/leads`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, { withCredentials: true });
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/leads/${leadId}/status`, 
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success('Status updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAction = async () => {
    if (!actionDialog.lead) return;
    setActionLoading(true);

    try {
      await axios.post(`${API_URL}/api/leads/${actionDialog.lead.id}/actions`, 
        { action_type: actionDialog.type, message: actionMessage },
        { withCredentials: true }
      );
      toast.success(`${actionDialog.type === 'sms' ? 'SMS' : actionDialog.type === 'email' ? 'Email' : actionDialog.type === 'docs_request' ? 'Document request' : 'Loan agreement'} sent successfully`);
      setActionDialog({ open: false, type: '', lead: null });
      setActionMessage('');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to perform action');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leads/export/csv`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export leads');
    }
  };

  const getActionTitle = (type) => {
    switch (type) {
      case 'sms': return 'Send SMS';
      case 'email': return 'Send Email';
      case 'docs_request': return 'Request Documents';
      case 'loan_agreement': return 'Send Loan Agreement';
      default: return 'Perform Action';
    }
  };

  return (
    <div data-testid="leads-management">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Lead Management</h1>
          <p className="text-slate-600 mt-1">{leads.length} leads found</p>
        </div>
        <Button onClick={handleExport} variant="outline" data-testid="export-leads-btn">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Search by name, business, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-leads-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(statusLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No leads found</h3>
            <p className="text-slate-500">Leads will appear here when submitted</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Contact</TableHead>
                  <TableHead>Business</TableHead>
                  <TableHead>Loan Details</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id} className="hover:bg-slate-50" data-testid={`lead-row-${lead.id}`}>
                    <TableCell>
                      <div>
                        <div className="font-medium text-slate-900">{lead.full_name}</div>
                        <div className="text-sm text-slate-500">{lead.email}</div>
                        <div className="text-sm text-slate-500">{lead.phone_number}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium text-slate-900">{lead.business_name}</div>
                        <div className="text-sm text-slate-500">{lead.business_type}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-bold text-slate-900">{formatCurrency(lead.loan_amount)}</div>
                        <div className="text-sm text-slate-500">{lead.loan_type} • {lead.funding_purpose}</div>
                        {lead.monthly_turnover && <div className="text-xs text-slate-400">Turnover: {lead.monthly_turnover}</div>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select 
                        value={lead.status} 
                        onValueChange={(value) => handleStatusChange(lead.id, value)}
                      >
                        <SelectTrigger className="w-36 h-8" data-testid={`status-select-${lead.id}`}>
                          <Badge className={`${statusColors[lead.status]} border font-medium`}>
                            {statusLabels[lead.status]}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(statusLabels).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(lead.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`actions-menu-${lead.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => setSelectedLead(lead)} data-testid={`view-details-${lead.id}`}>
                            <Eye className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setSelectedLead(lead)} data-testid={`view-messages-${lead.id}`}>
                            <MessageSquare className="w-4 h-4 mr-2" /> Messages
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setActionDialog({ open: true, type: 'sms', lead })} data-testid={`send-sms-${lead.id}`}>
                            <MessageSquare className="w-4 h-4 mr-2" /> Send SMS
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActionDialog({ open: true, type: 'email', lead })} data-testid={`send-email-${lead.id}`}>
                            <Mail className="w-4 h-4 mr-2" /> Send Email
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActionDialog({ open: true, type: 'docs_request', lead })} data-testid={`request-docs-${lead.id}`}>
                            <FileUp className="w-4 h-4 mr-2" /> Request Documents
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setActionDialog({ open: true, type: 'loan_agreement', lead })} data-testid={`send-agreement-${lead.id}`}>
                            <FileSignature className="w-4 h-4 mr-2" /> Send Loan Agreement
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Lead Details Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Lead Details</DialogTitle>
            <DialogDescription className="sr-only">View lead information, documents, and messages</DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-6" data-testid="lead-details-dialog">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Full Name</p>
                  <p className="font-medium">{selectedLead.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Business Name</p>
                  <p className="font-medium">{selectedLead.business_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{selectedLead.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium">{selectedLead.phone_number}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Business Type</p>
                  <p className="font-medium">{selectedLead.business_type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Company Reg</p>
                  <p className="font-medium">{selectedLead.company_reg_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Industry/Sector</p>
                  <p className="font-medium">{selectedLead.industry_sector || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Time in Business</p>
                  <p className="font-medium">{selectedLead.time_in_business}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Monthly Turnover</p>
                  <p className="font-medium">{selectedLead.monthly_turnover || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Annual Revenue</p>
                  <p className="font-medium">{selectedLead.annual_revenue || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-slate-500">Trading Address</p>
                  <p className="font-medium">{selectedLead.trading_address}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">CCJ/Defaults</p>
                  <p className="font-medium">{selectedLead.has_ccj_defaults || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Loan Amount</p>
                  <p className="font-medium text-blue-700">{formatCurrency(selectedLead.loan_amount)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Loan Type</p>
                  <p className="font-medium">{selectedLead.loan_type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Funding Purpose</p>
                  <p className="font-medium">{selectedLead.funding_purpose}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Preferred Contact Time</p>
                  <p className="font-medium">{selectedLead.preferred_contact_time || 'Anytime'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Marketing Opt-in</p>
                  <p className="font-medium">{selectedLead.marketing_opt_in ? 'Yes' : 'No'}</p>
                </div>
              </div>

              {selectedLead.actions && selectedLead.actions.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Action History</h4>
                  <div className="space-y-2">
                    {selectedLead.actions.map((action, i) => (
                      <div key={i} className="bg-slate-50 rounded-lg p-3 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium capitalize">{action.type.replace('_', ' ')}</span>
                          <span className="text-slate-500">{formatDate(action.performed_at)}</span>
                        </div>
                        {action.message && <p className="text-slate-600 mt-1">{action.message}</p>}
                        <p className="text-slate-400 text-xs mt-1">By {action.performed_by}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents Section */}
              {selectedLead.documents && selectedLead.documents.length > 0 && (
                <LeadDocumentsAdmin leadId={selectedLead.id} documents={selectedLead.documents} />
              )}

              {/* Messages Section */}
              <LeadMessagesAdmin leadId={selectedLead.id} leadName={selectedLead.full_name} />

              {selectedLead.notes && selectedLead.notes.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Notes</h4>
                  <div className="space-y-2">
                    {selectedLead.notes.map((note, i) => (
                      <div key={i} className="bg-slate-50 rounded-lg p-3 text-sm">
                        <p className="text-slate-700">{note.note}</p>
                        <p className="text-slate-400 text-xs mt-1">By {note.created_by} • {formatDate(note.created_at)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, type: '', lead: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{getActionTitle(actionDialog.type)}</DialogTitle>
            <DialogDescription>
              {actionDialog.lead && `Sending to ${actionDialog.lead.full_name} (${actionDialog.type === 'sms' ? actionDialog.lead.phone_number : actionDialog.lead.email})`}
            </DialogDescription>
          </DialogHeader>
          {(actionDialog.type === 'sms' || actionDialog.type === 'email') && (
            <div className="py-4 space-y-4">
              {/* Quick Reply Templates */}
              <div data-testid="quick-reply-templates">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Quick Templates</p>
                <div className="flex flex-wrap gap-1.5">
                  {(actionDialog.type === 'sms' ? [
                    { label: 'Welcome', text: `Hi {{name}}, thanks for choosing QuickFinance! Your dedicated advisor will be in touch within 4 hours. Track your application at quickfinance.io/portal` },
                    { label: 'Docs Needed', text: `Hi {{name}}, we need a few documents to progress your application. Please upload them via your portal at quickfinance.io/portal/documents` },
                    { label: 'Status Update', text: `Hi {{name}}, quick update on your QuickFinance application - it's progressing well and we should have news for you shortly.` },
                    { label: 'Approval', text: `Hi {{name}}, great news! Your funding application has been approved. Please check your portal to review and sign your agreement.` },
                    { label: 'Follow Up', text: `Hi {{name}}, just checking in on your application. Is there anything you need help with? Reply here or call us on 0800 123 4567.` },
                    { label: 'Agreement Ready', text: `Hi {{name}}, your loan agreement is ready to review and sign. Log in to your portal at quickfinance.io/portal/agreements` },
                  ] : [
                    { label: 'Welcome', text: `Hi {{name}},\n\nThank you for your application with QuickFinance. A dedicated advisor has been assigned to your case and will be in touch within 4 hours.\n\nIn the meantime, you can track your application progress via your client portal.\n\nBest regards,\nQuickFinance Team` },
                    { label: 'Docs Needed', text: `Hi {{name}},\n\nTo progress your funding application, we need a few documents from you. Please log into your portal and upload the required items at your earliest convenience.\n\nIf you have any questions about what's needed, just reply to this email.\n\nBest regards,\nQuickFinance Team` },
                    { label: 'Status Update', text: `Hi {{name}},\n\nJust a quick update on your application — everything is progressing well and our team is reviewing your details. We'll be in touch shortly with next steps.\n\nBest regards,\nQuickFinance Team` },
                    { label: 'Approval', text: `Hi {{name}},\n\nGreat news! Your funding application has been approved. Please log into your portal to review the offer details and sign your agreement.\n\nIf you have any questions, don't hesitate to reach out.\n\nBest regards,\nQuickFinance Team` },
                    { label: 'Follow Up', text: `Hi {{name}},\n\nWe wanted to check in on your application. If there's anything you need help with or any questions, we're here for you.\n\nFeel free to reply to this email or call us directly.\n\nBest regards,\nQuickFinance Team` },
                    { label: 'Agreement Ready', text: `Hi {{name}},\n\nYour loan agreement is now ready for review and signature. Please log into your client portal to review the terms and complete the signing process.\n\nBest regards,\nQuickFinance Team` },
                  ]).map((tpl) => {
                    const firstName = actionDialog.lead?.full_name?.split(' ')[0] || 'there';
                    return (
                      <button
                        key={tpl.label}
                        type="button"
                        onClick={() => setActionMessage(tpl.text.replace(/\{\{name\}\}/g, firstName))}
                        className="text-[11px] font-medium px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
                        data-testid={`template-${tpl.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        {tpl.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Textarea
                placeholder="Enter your message..."
                value={actionMessage}
                onChange={(e) => setActionMessage(e.target.value)}
                rows={4}
                data-testid="action-message-input"
              />
              {actionDialog.type === 'sms' && (
                <div data-testid="sms-preview-panel">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Client Preview</p>
                  <div className="mx-auto max-w-[280px] rounded-[24px] border-[3px] border-slate-800 bg-slate-900 p-2 shadow-lg">
                    {/* Phone status bar */}
                    <div className="flex items-center justify-between px-4 pt-1 pb-2">
                      <span className="text-[10px] text-slate-400 font-medium">9:41</span>
                      <div className="flex items-center gap-1">
                        <div className="w-3.5 h-2 border border-slate-400 rounded-sm relative"><div className="absolute inset-[1px] right-[2px] bg-slate-400 rounded-[1px]"></div></div>
                      </div>
                    </div>
                    {/* Chat header */}
                    <div className="bg-slate-100 rounded-t-[16px] px-3 py-2 flex items-center gap-2 border-b border-slate-200">
                      <div className="w-7 h-7 rounded-full bg-blue-700 flex items-center justify-center text-[10px] font-bold text-white">QF</div>
                      <div>
                        <div className="text-[11px] font-semibold text-slate-900">QuickFin</div>
                        <div className="text-[9px] text-slate-400">SMS</div>
                      </div>
                    </div>
                    {/* Message area */}
                    <div className="bg-slate-100 rounded-b-[16px] px-3 py-3 min-h-[120px] flex flex-col justify-end">
                      {actionMessage ? (
                        <div className="self-start max-w-[220px]">
                          <div className="bg-white rounded-2xl rounded-tl-md px-3 py-2 shadow-sm border border-slate-200">
                            <p className="text-[11px] text-slate-800 leading-relaxed whitespace-pre-wrap break-words">{actionMessage}</p>
                          </div>
                          <p className="text-[8px] text-slate-400 mt-0.5 ml-1">Now</p>
                        </div>
                      ) : (
                        <p className="text-[10px] text-slate-400 text-center italic">Type a message to see preview</p>
                      )}
                    </div>
                  </div>
                  {actionMessage && (
                    <p className="text-center text-[10px] text-slate-400 mt-2">{actionMessage.length} / 160 characters{actionMessage.length > 160 ? ` (${Math.ceil(actionMessage.length / 153)} segments)` : ''}</p>
                  )}
                </div>
              )}
            </div>
          )}
          {(actionDialog.type === 'docs_request') && (
            <div className="py-4">
              <p className="text-sm text-slate-600">
                A document request email and SMS will be sent to the client with instructions on how to upload their required documents.
              </p>
            </div>
          )}
          {actionDialog.type === 'loan_agreement' && actionDialog.lead && (
            <AgreementForm 
              lead={actionDialog.lead} 
              onClose={() => setActionDialog({ open: false, type: '', lead: null })} 
              onSuccess={fetchLeads} 
            />
          )}
          {actionDialog.type !== 'loan_agreement' && (
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, type: '', lead: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction} 
              disabled={actionLoading || ((actionDialog.type === 'sms' || actionDialog.type === 'email') && !actionMessage)}
              className="bg-blue-700 hover:bg-blue-800"
              data-testid="confirm-action-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Send
            </Button>
          </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Lead Documents Admin Component
function LeadDocumentsAdmin({ leadId, documents }) {
  const [docs, setDocs] = useState(documents || []);
  const [updating, setUpdating] = useState(null);

  const handleDocAction = async (docType, action, reason) => {
    setUpdating(docType);
    try {
      await axios.patch(`${API_URL}/api/leads/${leadId}/documents/${docType}`,
        { status: action, reason },
        { withCredentials: true }
      );
      setDocs(prev => prev.map(d => d.type === docType ? { ...d, status: action } : d));
      toast.success(`Document ${action}`);
    } catch (error) {
      toast.error('Failed to update document');
    } finally {
      setUpdating(null);
    }
  };

  const handleDownload = async (docType) => {
    try {
      const response = await axios.get(`${API_URL}/api/leads/${leadId}/documents/${docType}/download`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${docType}_${leadId.slice(0, 8)}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Document not available for download');
    }
  };

  const docStatusColors = {
    required: 'bg-red-100 text-red-700',
    uploaded: 'bg-amber-100 text-amber-700',
    under_review: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700'
  };

  return (
    <div data-testid="lead-documents-admin">
      <h4 className="font-semibold text-slate-900 mb-3">Documents ({docs.filter(d => d.status !== 'required').length}/{docs.length} submitted)</h4>
      <div className="space-y-2">
        {docs.map((doc) => (
          <div key={doc.type} className="bg-slate-50 rounded-lg p-3 flex items-center justify-between" data-testid={`admin-doc-${doc.type}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{doc.name}</p>
              <Badge className={`mt-1 text-xs ${docStatusColors[doc.status] || 'bg-slate-100'}`}>
                {doc.status?.replace('_', ' ')}
              </Badge>
            </div>
            <div className="flex items-center gap-2 ml-3 flex-shrink-0">
              {doc.filename && (
                <Button variant="ghost" size="sm" onClick={() => handleDownload(doc.type)} data-testid={`download-doc-${doc.type}`}>
                  <Download className="w-4 h-4" />
                </Button>
              )}
              {(doc.status === 'uploaded' || doc.status === 'under_review') && (
                <>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                    disabled={updating === doc.type}
                    onClick={() => handleDocAction(doc.type, 'approved')}
                    data-testid={`approve-doc-${doc.type}`}
                  >
                    {updating === doc.type ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                    disabled={updating === doc.type}
                    onClick={() => handleDocAction(doc.type, 'rejected', 'Please re-upload a clearer copy.')}
                    data-testid={`reject-doc-${doc.type}`}
                  >
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Lead Messages Admin Component
function LeadMessagesAdmin({ leadId, leadName }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = React.useRef(null);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/leads/${leadId}/messages`, { withCredentials: true });
      setMessages(res.data.messages || []);
    } catch (e) {
      // Silently handle - no messages yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMessages(); }, [leadId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/leads/${leadId}/messages`, 
        { content: newMessage.trim() },
        { withCredentials: true }
      );
      setNewMessage('');
      fetchMessages();
      toast.success('Message sent');
    } catch (e) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-blue-700" />
      </div>
    );
  }

  return (
    <div data-testid="lead-messages-admin">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-slate-900">Messages</h4>
        <Button variant="ghost" size="sm" onClick={fetchMessages} data-testid="refresh-messages-btn">
          <RefreshCw className="w-3 h-3" />
        </Button>
      </div>

      {/* Messages list */}
      <div className="bg-slate-50 rounded-lg p-3 max-h-64 overflow-y-auto space-y-3 mb-3" data-testid="messages-list">
        {messages.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No messages yet. Start the conversation below.</p>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`}
              data-testid={`message-${msg.id}`}
            >
              <div className={`max-w-[75%] rounded-lg px-3 py-2 ${
                msg.sender_role === 'admin'
                  ? 'bg-blue-700 text-white'
                  : 'bg-white border border-slate-200 text-slate-800'
              }`}>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                <div className={`flex items-center gap-1 mt-1 ${
                  msg.sender_role === 'admin' ? 'text-blue-200' : 'text-slate-400'
                }`}>
                  <span className="text-[10px]">{msg.sender_name}</span>
                  <span className="text-[10px]">·</span>
                  <span className="text-[10px]">{new Date(msg.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply input */}
      <div className="flex gap-2" data-testid="admin-reply-input">
        <Textarea
          placeholder={`Reply to ${leadName?.split(' ')[0] || 'client'}...`}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          className="flex-1 resize-none text-sm"
          data-testid="admin-message-input"
        />
        <Button
          onClick={handleSend}
          disabled={sending || !newMessage.trim()}
          className="bg-blue-700 hover:bg-blue-800 self-end"
          size="sm"
          data-testid="send-admin-message-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}


// Loan Agreement Creation Form
function AgreementForm({ lead, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    loan_amount: lead.loan_amount,
    interest_rate: 9.9,
    term_months: 36,
    arrangement_fee: 0
  });
  const [loading, setLoading] = useState(false);

  const monthlyPayment = (() => {
    const r = formData.interest_rate / 100 / 12;
    const n = formData.term_months;
    const p = formData.loan_amount;
    if (r === 0) return p / n;
    return p * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  })();
  const totalRepayable = monthlyPayment * formData.term_months + formData.arrangement_fee;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads/${lead.id}/agreement`, {
        lead_id: lead.id,
        loan_amount: formData.loan_amount,
        interest_rate: formData.interest_rate,
        term_months: formData.term_months,
        monthly_payment: Math.round(monthlyPayment * 100) / 100,
        total_repayable: Math.round(totalRepayable * 100) / 100,
        arrangement_fee: formData.arrangement_fee
      }, { withCredentials: true });
      toast.success('Agreement created and sent to client!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error('Failed to create agreement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="agreement-form">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-slate-700">Loan Amount</label>
          <Input type="number" value={formData.loan_amount} onChange={(e) => setFormData(p => ({...p, loan_amount: Number(e.target.value)}))} className="mt-1" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Interest Rate (% APR)</label>
          <Input type="number" step="0.1" value={formData.interest_rate} onChange={(e) => setFormData(p => ({...p, interest_rate: Number(e.target.value)}))} className="mt-1" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Term (months)</label>
          <Input type="number" value={formData.term_months} onChange={(e) => setFormData(p => ({...p, term_months: Number(e.target.value)}))} className="mt-1" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Arrangement Fee</label>
          <Input type="number" value={formData.arrangement_fee} onChange={(e) => setFormData(p => ({...p, arrangement_fee: Number(e.target.value)}))} className="mt-1" />
        </div>
      </div>
      <div className="bg-slate-50 rounded-lg p-4">
        <div className="flex justify-between text-sm mb-1"><span className="text-slate-500">Monthly Payment</span><span className="font-bold">{formatCurrency(monthlyPayment)}</span></div>
        <div className="flex justify-between text-sm mb-1"><span className="text-slate-500">Total Repayable</span><span className="font-bold">{formatCurrency(totalRepayable)}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-500">Total Interest</span><span className="font-medium text-slate-600">{formatCurrency(totalRepayable - formData.loan_amount)}</span></div>
      </div>
      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading} className="bg-blue-700 hover:bg-blue-800" data-testid="create-agreement-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSignature className="w-4 h-4 mr-2" />}
          Create & Send Agreement
        </Button>
      </div>
    </div>
  );
}

// Agreements Management Component (Admin)
function AgreementsManagement() {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countersigning, setCountersigning] = useState(null);

  useEffect(() => { fetchAgreements(); }, []);

  const fetchAgreements = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/agreements`, { withCredentials: true });
      setAgreements(res.data.agreements || []);
    } catch (e) { toast.error('Failed to load agreements'); }
    finally { setLoading(false); }
  };

  const handleCountersign = async (agreementId) => {
    setCountersigning(agreementId);
    try {
      await axios.post(`${API_URL}/api/agreements/${agreementId}/countersign`, {}, { withCredentials: true });
      toast.success('Agreement countersigned! Client notified.');
      fetchAgreements();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to countersign';
      toast.error(typeof msg === 'string' ? msg : 'Failed');
    }
    finally { setCountersigning(null); }
  };

  const handleDownload = async (agreementId) => {
    try {
      const res = await axios.get(`${API_URL}/api/agreements/${agreementId}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `agreement_${agreementId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { toast.error('Failed to download'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-700" /></div>;

  return (
    <div data-testid="agreements-management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Agreements</h1>
          <p className="text-slate-500 text-sm">Manage loan agreements and countersignatures</p>
        </div>
        <Button variant="outline" size="icon" onClick={fetchAgreements}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {agreements.length === 0 ? (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-xl">
          <FileSignature className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No agreements yet. Create one from a lead's action menu.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {agreements.map((a) => (
            <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`admin-agreement-${a.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900">{a.client_name} &middot; {a.business_name}</h3>
                  <p className="text-sm text-slate-500">Created {formatDate(a.created_at)}</p>
                </div>
                <div className="flex gap-2">
                  {a.signed && !a.countersigned && (
                    <Badge className="bg-amber-100 text-amber-700">Awaiting Countersign</Badge>
                  )}
                  {a.countersigned && (
                    <Badge className="bg-green-100 text-green-700">Fully Executed</Badge>
                  )}
                  {!a.signed && (
                    <Badge className="bg-blue-100 text-blue-700">Awaiting Client Signature</Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-3 bg-slate-50 rounded-lg text-sm">
                <div><span className="text-slate-500">Loan</span><p className="font-bold">{formatCurrency(a.loan_amount)}</p></div>
                <div><span className="text-slate-500">Rate</span><p className="font-bold">{a.interest_rate}% APR</p></div>
                <div><span className="text-slate-500">Term</span><p className="font-bold">{a.term_months} months</p></div>
                <div><span className="text-slate-500">Monthly</span><p className="font-bold text-blue-700">{formatCurrency(a.monthly_payment)}</p></div>
              </div>

              {a.signature && (
                <p className="text-xs text-slate-500 mb-3">Signed by {a.signature.name} on {a.signature.date}</p>
              )}
              {a.countersign && (
                <p className="text-xs text-slate-500 mb-3">Countersigned by {a.countersign.name} on {a.countersign.date?.split('T')[0]}</p>
              )}

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleDownload(a.id)}>
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
                {a.signed && !a.countersigned && (
                  <Button size="sm" className="bg-blue-700 hover:bg-blue-800"
                    disabled={countersigning === a.id}
                    onClick={() => handleCountersign(a.id)}
                    data-testid={`countersign-${a.id}`}
                  >
                    {countersigning === a.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileSignature className="w-4 h-4 mr-1" />}
                    Countersign
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Messages Inbox Component
function MessagesInbox() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = React.useRef(null);

  useEffect(() => { fetchConversations(); }, []);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/messages`, { withCredentials: true });
      setConversations(res.data.conversations || []);
    } catch (e) { toast.error('Failed to load messages'); }
    finally { setLoading(false); }
  };

  const openConversation = async (conv) => {
    setSelectedConversation(conv);
    setLoadingMessages(true);
    try {
      const res = await axios.get(`${API_URL}/api/leads/${conv.lead_id}/messages`, { withCredentials: true });
      setMessages(res.data.messages || []);
      // Refresh conversations to update unread counts
      fetchConversations();
    } catch (e) { toast.error('Failed to load messages'); }
    finally { setLoadingMessages(false); }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/leads/${selectedConversation.lead_id}/messages`,
        { content: newMessage.trim() }, { withCredentials: true });
      setNewMessage('');
      // Refresh messages
      const res = await axios.get(`${API_URL}/api/leads/${selectedConversation.lead_id}/messages`, { withCredentials: true });
      setMessages(res.data.messages || []);
      fetchConversations();
    } catch (e) { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-700" /></div>;

  return (
    <div data-testid="messages-inbox">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Messages</h1>
          <p className="text-slate-500 text-sm">All client conversations in one place</p>
        </div>
        <Button variant="outline" size="icon" onClick={fetchConversations} data-testid="refresh-inbox-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" style={{ height: 'calc(100vh - 220px)' }}>
        <div className="flex h-full">
          {/* Conversation List */}
          <div className={`${selectedConversation ? 'hidden md:block' : ''} w-full md:w-96 border-r border-slate-200 overflow-y-auto`} data-testid="conversation-list">
            {conversations.length === 0 ? (
              <div className="text-center py-16">
                <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No conversations yet</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.lead_id}
                  onClick={() => openConversation(conv)}
                  className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                    selectedConversation?.lead_id === conv.lead_id ? 'bg-blue-50 border-l-2 border-l-blue-700' : ''
                  }`}
                  data-testid={`conversation-${conv.lead_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900 text-sm truncate">{conv.lead_name}</p>
                        {conv.unread_count > 0 && (
                          <span className="flex-shrink-0 bg-blue-700 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{conv.unread_count}</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{conv.business_name}</p>
                    </div>
                    <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">
                      {new Date(conv.last_message_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 truncate">
                    {conv.last_message_role === 'admin' ? 'You: ' : ''}{conv.last_message}
                  </p>
                </div>
              ))
            )}
          </div>

          {/* Chat View */}
          <div className={`${selectedConversation ? '' : 'hidden md:flex'} flex-1 flex flex-col`}>
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center gap-3">
                  <button className="md:hidden p-1 text-slate-600" onClick={() => setSelectedConversation(null)} data-testid="back-to-conversations">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm">{selectedConversation.lead_name}</p>
                    <p className="text-xs text-slate-500">{selectedConversation.business_name} &middot; {selectedConversation.lead_email}</p>
                  </div>
                  <Badge className={`${statusColors[selectedConversation.lead_status] || 'bg-slate-100'} border text-xs`}>
                    {statusLabels[selectedConversation.lead_status] || selectedConversation.lead_status}
                  </Badge>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="inbox-messages">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-blue-700" /></div>
                  ) : messages.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">No messages in this conversation yet.</p>
                  ) : (
                    messages.map((msg) => (
                      <div key={msg.id} className={`flex ${msg.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`} data-testid={`inbox-msg-${msg.id}`}>
                        <div className={`max-w-[70%] rounded-lg px-3 py-2 ${
                          msg.sender_role === 'admin' ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-800'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <div className={`flex items-center gap-1 mt-1 ${msg.sender_role === 'admin' ? 'text-blue-200' : 'text-slate-400'}`}>
                            <span className="text-[10px]">{msg.sender_name}</span>
                            <span className="text-[10px]">&middot;</span>
                            <span className="text-[10px]">{new Date(msg.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply Input */}
                <div className="p-3 border-t border-slate-200 bg-white flex gap-2" data-testid="inbox-reply">
                  <Textarea
                    placeholder={`Reply to ${selectedConversation.lead_name?.split(' ')[0]}...`}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={2}
                    className="flex-1 resize-none text-sm"
                    data-testid="inbox-message-input"
                  />
                  <Button onClick={handleSend} disabled={sending || !newMessage.trim()} className="bg-blue-700 hover:bg-blue-800 self-end" size="sm" data-testid="inbox-send-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Select a conversation to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Audit Log Component
function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => { fetchLog(); }, []);

  const fetchLog = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/audit-log?limit=200`, { withCredentials: true });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch (e) { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  };

  const actionIcons = {
    status_change: <RefreshCw className="w-4 h-4 text-blue-600" />,
    document_approved: <CheckCircle2 className="w-4 h-4 text-green-600" />,
    document_rejected: <Circle className="w-4 h-4 text-red-600" />,
    action_sms: <MessageSquare className="w-4 h-4 text-purple-600" />,
    action_email: <Mail className="w-4 h-4 text-indigo-600" />,
    action_docs_request: <FileUp className="w-4 h-4 text-orange-600" />,
    action_loan_agreement: <FileSignature className="w-4 h-4 text-teal-600" />,
    agreement_created: <FileText className="w-4 h-4 text-blue-600" />,
    agreement_countersigned: <CheckCircle2 className="w-4 h-4 text-green-600" />
  };

  const actionLabels = {
    status_change: 'Status Change',
    document_approved: 'Document Approved',
    document_rejected: 'Document Rejected',
    action_sms: 'SMS Sent',
    action_email: 'Email Sent',
    action_docs_request: 'Documents Requested',
    action_loan_agreement: 'Agreement Action',
    agreement_created: 'Agreement Created',
    agreement_countersigned: 'Agreement Countersigned'
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-700" /></div>;

  return (
    <div data-testid="audit-log">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Audit Log</h1>
          <p className="text-slate-500 text-sm">{total} events recorded</p>
        </div>
        <Button variant="outline" size="icon" onClick={fetchLog} data-testid="refresh-audit-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-xl">
          <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No audit events yet. Actions will be logged here automatically.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Performed By</TableHead>
                  <TableHead>Date & Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id} className="hover:bg-slate-50" data-testid={`audit-entry-${entry.id}`}>
                    <TableCell>{actionIcons[entry.action] || <Circle className="w-4 h-4 text-slate-400" />}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs font-medium">
                        {actionLabels[entry.action] || entry.action.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-md">
                      <p className="text-sm text-slate-700 truncate">{entry.details}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm font-medium text-slate-900">{entry.user_name}</p>
                      <p className="text-xs text-slate-400">{entry.user_role}</p>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                      {formatDate(entry.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}


// Main Dashboard Component
export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Overview', icon: LayoutDashboard },
    { path: '/dashboard/leads', label: 'Leads', icon: Users },
    { path: '/dashboard/messages', label: 'Messages', icon: Inbox },
    { path: '/dashboard/agreements', label: 'Agreements', icon: FileSignature },
    { path: '/dashboard/audit-log', label: 'Audit Log', icon: History },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-2 px-6 py-5 border-b border-slate-800">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl font-['Outfit']">Q</span>
            </div>
            <span className="text-xl font-bold text-white font-['Outfit']">QuickFinance</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="px-4 py-4 border-t border-slate-800">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">{user?.name?.charAt(0) || 'A'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800 mt-2"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
            <button
              className="lg:hidden p-2 text-slate-600"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-sidebar-btn"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm text-slate-600 hover:text-blue-700">
                View Website
              </Link>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Routes>
            <Route path="/" element={<DashboardOverview />} />
            <Route path="/leads" element={<LeadsManagement />} />
            <Route path="/messages" element={<MessagesInbox />} />
            <Route path="/agreements" element={<AgreementsManagement />} />
            <Route path="/audit-log" element={<AuditLog />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
